import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import { Save, AlertCircle, CheckCircle, FileCode, Loader2, Plus } from 'lucide-react';
import { policyApi } from '../api/client';
import { PolicyImport } from './PolicyImport';
import type { PolicyRule } from '../types';

// Loading skeleton
function PolicySkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
          <div className="h-4 w-32 bg-slate-700 rounded mb-2"></div>
          <div className="h-3 w-48 bg-slate-700/50 rounded"></div>
        </div>
      ))}
    </div>
  );
}

export function PolicyEditor() {
  const queryClient = useQueryClient();
  const [selectedRule, setSelectedRule] = useState<PolicyRule | null>(null);
  const [yamlContent, setYamlContent] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    errors?: string[];
  } | null>(null);

  const { data: rules, isLoading } = useQuery({
    queryKey: ['policies'],
    queryFn: policyApi.list,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, yaml }: { id: string; yaml: string }) => policyApi.update(id, yaml),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      setSelectedRule(null);
      setYamlContent('');
    },
  });

  const handleValidate = async () => {
    if (!yamlContent) return;
    const result = await policyApi.validate(yamlContent);
    setValidationResult(result);
  };

  const handleSave = () => {
    if (!selectedRule || !validationResult?.valid) return;
    updateMutation.mutate({ id: selectedRule.id, yaml: yamlContent });
  };

  const handleImport = (yaml: string, name: string) => {
    // When a policy is imported, we load it into the editor as a new policy
    // In a real app, this would call an API to create the policy
    setYamlContent(yaml);
    setSelectedRule({
      id: `imported-${Date.now()}`,
      name: name,
      displayRule: 'Imported - review and save',
      enabled: false,
      yamlContent: yaml,
    } as PolicyRule);
    setValidationResult(null);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <h3 className="font-semibold text-white mb-4">Policy Rules</h3>
          <PolicySkeleton />
        </div>
        <div className="col-span-2 card">
          <div className="text-center text-slate-500 py-20">
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
            <p>Loading policies...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      {/* Policy Import Modal */}
      <PolicyImport
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImport={handleImport}
      />

      {/* Rule list */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">Policy Rules</h3>
          <button
            onClick={() => setShowImport(true)}
            className="btn btn-primary btn-sm flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            Import
          </button>
        </div>
        <div className="space-y-2">
          {rules?.map(rule => (
            <button
              key={rule.id}
              onClick={() => {
                setSelectedRule(rule);
                setYamlContent(rule.yamlContent);
                setValidationResult(null);
              }}
              className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${selectedRule?.id === rule.id
                ? 'bg-primary-500/20 border-primary-500/50 shadow-lg shadow-primary-500/10'
                : 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-700/50 hover:border-slate-600/50'
                }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-white">{rule.name}</p>
                  <p className="text-sm text-slate-400 mt-1">{rule.displayRule}</p>
                </div>
                <span
                  className={`badge ${rule.enabled ? 'badge-success' : 'bg-slate-700 text-slate-400 border-slate-600'
                    }`}
                >
                  {rule.enabled ? 'ON' : 'OFF'}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="col-span-2 card">
        {selectedRule ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center">
                  <FileCode className="w-5 h-5 text-primary-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{selectedRule.name}</h3>
                  <p className="text-xs text-slate-400">Edit policy YAML</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleValidate}
                  className="btn btn-ghost"
                >
                  Validate
                </button>
                <button
                  onClick={handleSave}
                  disabled={!validationResult?.valid || updateMutation.isPending}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {updateMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
              </div>
            </div>

            {validationResult && (
              <div
                className={`mb-4 p-3 rounded-lg flex items-start gap-3 border ${validationResult.valid
                  ? 'bg-success-500/10 border-success-500/30 text-success-300'
                  : 'bg-danger-500/10 border-danger-500/30 text-danger-300'
                  }`}
              >
                {validationResult.valid ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="font-medium">
                    {validationResult.valid ? 'Valid YAML' : 'Invalid YAML'}
                  </p>
                  {validationResult.errors && (
                    <ul className="text-sm mt-1 space-y-1 opacity-80">
                      {validationResult.errors.map((error, i) => (
                        <li key={i}>• {error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <div className="border border-slate-700/50 rounded-xl overflow-hidden">
              <Editor
                height="500px"
                defaultLanguage="yaml"
                value={yamlContent}
                onChange={value => setYamlContent(value || '')}
                theme="vs-dark"
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  padding: { top: 16, bottom: 16 },
                  renderLineHighlight: 'all',
                  cursorBlinking: 'smooth',
                }}
              />
            </div>
          </>
        ) : (
          <div className="text-center text-slate-500 py-20">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-800/50 rounded-2xl flex items-center justify-center">
              <FileCode className="w-8 h-8 text-slate-600" />
            </div>
            <p className="text-slate-400">Select a policy rule to edit</p>
            <p className="text-sm text-slate-500 mt-1">Choose from the list on the left</p>
          </div>
        )}
      </div>
    </div>
  );
}
